/**
 * Member Import Modal with CSV Preview and Bulk Edit
 * Features:
 * - CSV file upload and parsing
 * - Auto-detection of columns (no manual mapping needed)
 * - Single gym selection for entire import
 * - Preview table with editable fields
 * - Find & Replace bulk update for plan mapping
 *
 * API: POST /member/importUsers/v2
 * Payload: { gym_id: "...", user_details: [{...}] }
 *
 * Flow: 4 steps
 * 1. Upload file
 * 2. Select gym + Preview + Map plans + Replace-existing toggle
 * 3. Preview Summary (dry-run) — shows would-insert / would-skip counts and reasons
 * 4. Results
 *
 * Created: February 2026
 */

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ReactModal from "react-modal";
import ModalHeader from "../ModalHeader";
import Loader from "../ui/Loader";
import SelectDropdown from "../components-react/SelectDropdown";
import { fetchGymData } from "../../data/APIs";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import { createFetchPlans } from "../../redux/reducers/postApiSlice";
import { BASE_URL } from "../../data/BaseUrl";
import { memberImportV2 } from "../../data/ApiEndpoints";
import "../../styles/member-import-modal.css";

const MemberImportModal = ({
  isOpen,
  onClose,
  userInfoData,
  onImportSuccess,
}) => {
  const dispatch = useDispatch();
  const gymData = useSelector(selectGymData);

  // Step management: 1=upload, 2=gym+preview+plan mapping, 3=preview-summary (dry-run), 4=results
  const [step, setStep] = useState(1);

  // Replace existing imported subscriptions (deletes old is_imported=1 rows for matching members in this gym)
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Dry-run preview data returned from backend (would-insert / would-skip counts + details)
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // File and CSV data
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);

  // Column mapping (auto-detected)
  const [columnMapping, setColumnMapping] = useState({
    name: "",
    email: "",
    mobile: "",
    plan: "",
    start_date: "",
    end_date: "",
    payment_date: "",
    price: "",
    transaction_id: "",
  });

  // Selected gym for entire import (API requires single gym_id at top level)
  const [selectedGym, setSelectedGym] = useState(null);

  // Plans data for selected gym
  const [plansData, setPlansData] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Find & Replace state for plans
  const [uniquePlanValues, setUniquePlanValues] = useState([]);
  const [planMappings, setPlanMappings] = useState({}); // { "csv_value": { label, value } }

  // Selected for bulk plan mapping
  const [selectedPlanValue, setSelectedPlanValue] = useState("");
  const [targetPlan, setTargetPlan] = useState(null);

  // Pagination for preview table
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setStep(1);
    setCsvFile(null);
    setCsvData([]);
    setColumnMapping({
      name: "",
      email: "",
      mobile: "",
      plan: "",
      start_date: "",
      end_date: "",
      payment_date: "",
      price: "",
      transaction_id: "",
    });
    setSelectedGym(null);
    setPlansData([]);
    setUniquePlanValues([]);
    setPlanMappings({});
    setSelectedPlanValue("");
    setTargetPlan(null);
    setCurrentPage(1);
    setImportResults(null);
    setIsImporting(false);
    setReplaceExisting(false);
    setPreviewData(null);
    setIsPreviewLoading(false);
    onClose();
  }, [onClose]);

  // Fetch gym data on mount
  useEffect(() => {
    if (isOpen && userInfoData?.token) {
      dispatch(fetchGymData(userInfoData.token));
    }
  }, [isOpen, userInfoData?.token, dispatch]);

  // Fetch plans when gym is selected
  useEffect(() => {
    if (selectedGym?.value) {
      fetchPlansForGym(selectedGym.value);
    }
  }, [selectedGym?.value]);

  // Fetch plans for a gym
  const fetchPlansForGym = async (gymId) => {
    setLoadingPlans(true);
    try {
      const result = await dispatch(
        createFetchPlans({
          userData: { gym_id: gymId, couple_included: 0 },
          TOKEN: userInfoData?.token,
        })
      );

      if (result?.payload?.status && result?.payload?.data?.length > 0) {
        const plans = result.payload.data.map((plan) => ({
          label: `${plan.plan_name} / ${plan.duration} Days`,
          value: plan.plan_uid,
          duration: plan.duration,
          info: plan,
        }));
        setPlansData(plans);
      } else {
        setPlansData([]);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlansData([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Parse CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  // Auto-detect column mappings based on header names
  const autoDetectColumns = (headers) => {
    const mapping = {
      name: "",
      email: "",
      mobile: "",
      plan: "",
      start_date: "",
      end_date: "",
      payment_date: "",
      price: "",
      transaction_id: "",
    };

    headers.forEach((header) => {
      const lowerHeader = header.toLowerCase();

      if (lowerHeader.includes("name") && !lowerHeader.includes("plan")) {
        if (!mapping.name) mapping.name = header;
      } else if (lowerHeader.includes("email") || lowerHeader.includes("e-mail")) {
        mapping.email = header;
      } else if (
        lowerHeader.includes("phone") ||
        lowerHeader.includes("mobile") ||
        lowerHeader.includes("contact")
      ) {
        mapping.mobile = header;
      } else if (lowerHeader.includes("plan") || lowerHeader.includes("membership") || lowerHeader.includes("package")) {
        mapping.plan = header;
      } else if (lowerHeader.includes("start") && lowerHeader.includes("date")) {
        mapping.start_date = header;
      } else if ((lowerHeader.includes("end") || lowerHeader.includes("expir")) && lowerHeader.includes("date")) {
        mapping.end_date = header;
      } else if ((lowerHeader.includes("payment") || lowerHeader.includes("paid")) && lowerHeader.includes("date")) {
        mapping.payment_date = header;
      } else if (lowerHeader.includes("price") || lowerHeader.includes("amount")) {
        mapping.price = header;
      } else if (lowerHeader.includes("transaction") || lowerHeader.includes("txn")) {
        mapping.transaction_id = header;
      }
    });

    return mapping;
  };

  // Validate that required columns were detected
  const validateRequiredColumns = (mapping) => {
    const missing = [];
    if (!mapping.mobile) missing.push("Mobile");
    if (!mapping.plan) missing.push("Plan");
    if (!mapping.start_date) missing.push("Start Date");
    if (!mapping.end_date) missing.push("End Date");
    if (!mapping.payment_date) missing.push("Payment Date");
    return missing;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validExtensions = [".csv", ".xlsx", ".xls"];
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please select a CSV or Excel file");
      return;
    }

    setCsvFile(file);

    if (fileExtension === ".csv") {
      parseCSV(file);
    } else {
      parseExcel(file);
    }
  };

  // Process file data after parsing
  const processFileData = (parsedHeaders, rows) => {
    setCsvData(rows);

    // Auto-detect columns
    const mapping = autoDetectColumns(parsedHeaders);
    setColumnMapping(mapping);

    // Validate required columns
    const missingColumns = validateRequiredColumns(mapping);
    if (missingColumns.length > 0) {
      toast.error(
        `Could not find these required columns: ${missingColumns.join(", ")}. ` +
        `Please make sure your file has headers like: mobile, plan, start_date, end_date`
      );
      return false;
    }

    // Validate date formats (DD-MM-YYYY)
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    const invalidDateRows = [];
    rows.forEach((row, index) => {
      const startDate = mapping.start_date ? row[mapping.start_date]?.trim() : "";
      const endDate = mapping.end_date ? row[mapping.end_date]?.trim() : "";
      const paymentDate = mapping.payment_date ? row[mapping.payment_date]?.trim() : "";
      if (startDate && !dateRegex.test(startDate)) {
        invalidDateRows.push({ row: index + 2, field: "start_date", value: startDate });
      }
      if (endDate && !dateRegex.test(endDate)) {
        invalidDateRows.push({ row: index + 2, field: "end_date", value: endDate });
      }
      if (paymentDate && !dateRegex.test(paymentDate)) {
        invalidDateRows.push({ row: index + 2, field: "payment_date", value: paymentDate });
      }
    });

    if (invalidDateRows.length > 0) {
      const firstFew = invalidDateRows.slice(0, 3).map(r => `Row ${r.row}: ${r.field} = "${r.value}"`).join(", ");
      toast.error(
        `${invalidDateRows.length} date(s) not in DD-MM-YYYY format. ${firstFew}. Please fix your file and re-upload.`
      );
      setCsvFile(null);
      return false;
    }

    // Extract unique plan values
    const planValues = new Set();
    rows.forEach((row) => {
      if (mapping.plan && row[mapping.plan]) {
        planValues.add(row[mapping.plan].trim());
      }
    });
    setUniquePlanValues(Array.from(planValues).sort());
    setPlanMappings({});

    toast.success(`Loaded ${rows.length} members from file`);
    setStep(2); // Go directly to gym + preview step
    return true;
  };

  // Parse CSV file
  const parseCSV = (file) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length < 2) {
          toast.error("File must have at least a header row and one data row");
          return;
        }

        const parsedHeaders = parseCSVLine(lines[0]);

        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length > 0 && values.some((v) => v.trim())) {
            const row = { _rowId: i };
            parsedHeaders.forEach((header, index) => {
              row[header] = values[index] || "";
            });
            rows.push(row);
          }
        }

        processFileData(parsedHeaders, rows);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse file. Please check the format.");
      }
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
    };

    reader.readAsText(file);
  };

  // Parse Excel file using xlsx library
  const parseExcel = async (file) => {
    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array", cellDates: true });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, dateNF: "dd-mm-yyyy" });

          if (jsonData.length < 2) {
            toast.error("File must have at least a header row and one data row");
            return;
          }

          const parsedHeaders = jsonData[0].map((h) => String(h || "").trim());

          const rows = [];
          for (let i = 1; i < jsonData.length; i++) {
            const values = jsonData[i];
            if (values && values.some((v) => v)) {
              const row = { _rowId: i };
              parsedHeaders.forEach((header, index) => {
                row[header] = values[index] !== undefined ? String(values[index]) : "";
              });
              rows.push(row);
            }
          }

          processFileData(parsedHeaders, rows);
        } catch (error) {
          console.error("Error parsing Excel:", error);
          toast.error("Failed to parse Excel file");
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      toast.error("Excel parsing not supported. Please use CSV format.");
    }
  };

  // Apply plan mapping for a specific CSV value
  const handleApplyPlanMapping = () => {
    if (!selectedPlanValue || !targetPlan) {
      toast.error("Please select both source value and target plan");
      return;
    }

    setPlanMappings((prev) => ({
      ...prev,
      [selectedPlanValue]: targetPlan,
    }));

    toast.success(`Mapped "${selectedPlanValue}" → ${targetPlan.label}`);
    setSelectedPlanValue("");
    setTargetPlan(null);
  };

  // Update individual row field
  const handleRowFieldChange = (rowIndex, field, value) => {
    setCsvData((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
  };

  // Get mapped plan_id for a row
  const getMappedPlanId = (row) => {
    if (!columnMapping.plan) return null;
    const csvValue = row[columnMapping.plan]?.trim();
    return planMappings[csvValue]?.value || null;
  };

  // Check if all required mappings are done
  const checkMappingsComplete = () => {
    const unmappedPlans = uniquePlanValues.filter((v) => !planMappings[v]);
    return { unmappedPlans };
  };

  // Build the request payload from current state (shared by dry-run and actual import)
  const buildPayload = (dryRun = false) => {
    const userDetails = csvData.map((row) => {
      const user = {
        mobile: columnMapping.mobile ? row[columnMapping.mobile]?.trim() : "",
        start_date: columnMapping.start_date ? row[columnMapping.start_date]?.trim() : "",
        end_date: columnMapping.end_date ? row[columnMapping.end_date]?.trim() : "",
        payment_date: columnMapping.payment_date ? row[columnMapping.payment_date]?.trim() : "",
        plan_id: getMappedPlanId(row) || "",
      };
      if (columnMapping.name && row[columnMapping.name]) user.name = row[columnMapping.name].trim();
      if (columnMapping.email && row[columnMapping.email]) user.email = row[columnMapping.email].trim();
      if (columnMapping.price && row[columnMapping.price]) user.price = parseFloat(row[columnMapping.price]) || 0;
      if (columnMapping.transaction_id && row[columnMapping.transaction_id]) {
        user.transaction_id = row[columnMapping.transaction_id].trim();
      }
      return user;
    });

    const validUsers = userDetails.filter(
      (user) => user.mobile && user.start_date && user.end_date && user.payment_date && user.plan_id
    );

    return {
      validUsers,
      payload: {
        gym_id: selectedGym?.value,
        user_details: validUsers,
        replace_existing: replaceExisting,
        dry_run: dryRun,
      },
    };
  };

  // Step 2 → Step 3: dry-run preview (no DB writes)
  const handlePreview = async () => {
    if (!selectedGym) {
      toast.error("Please select a gym");
      return;
    }
    const { unmappedPlans } = checkMappingsComplete();
    if (uniquePlanValues.length > 0 && unmappedPlans.length > 0) {
      toast.error(`Please map all plan values. Unmapped: ${unmappedPlans.join(", ")}`);
      return;
    }

    const { validUsers, payload } = buildPayload(true);
    if (validUsers.length === 0) {
      toast.error("No valid rows to preview. Each row needs: mobile, start_date, end_date, payment_date, and plan");
      return;
    }

    setIsPreviewLoading(true);
    try {
      const response = await fetch(`${BASE_URL}${memberImportV2}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfoData?.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status) {
        setPreviewData(result.data);
        setStep(3);
      } else {
        toast.error(result.message || "Preview failed");
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error("Failed to generate preview. Please try again.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Step 3 → Step 4: actual import (writes to DB)
  const handleImport = async () => {
    const { validUsers, payload } = buildPayload(false);
    if (validUsers.length === 0) {
      toast.error("No valid rows to import.");
      return;
    }

    setIsImporting(true);
    try {
      const response = await fetch(`${BASE_URL}${memberImportV2}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfoData?.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status) {
        setImportResults({
          success: true,
          message: result.message || "Import completed successfully!",
          data: result.data,
        });
        setStep(4);
        toast.success(result.message || "Members imported successfully!");
        if (onImportSuccess) onImportSuccess();
      } else {
        toast.error(result.message || "Import failed!");
        setImportResults({
          success: false,
          message: result.message || "Import failed",
          data: result.data,
        });
        setStep(4);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import members. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  // Render Step 1: File Upload
  const renderUploadStep = () => (
    <div className="member-import-step">
      <div className="upload-info-box">
        <h4>How to Prepare Your File</h4>
        <p className="upload-intro">
          Create a file in <strong>Excel</strong> or <strong>CSV</strong> format with your member data.
          The first row should contain column names (headers).
        </p>

        {/* Required Fields */}
        <div className="fields-section">
          <h5 className="fields-title required-title">Required Information</h5>
          <div className="fields-list">
            <div className="field-item required">
              <span className="field-name">Mobile</span>
              <span className="field-desc">10-digit phone number (e.g., 9876543210)</span>
            </div>
            <div className="field-item required">
              <span className="field-name">Plan</span>
              <span className="field-desc">Plan name like "Monthly", "Quarterly", "Annual"</span>
            </div>
            <div className="field-item required">
              <span className="field-name">Start Date</span>
              <span className="field-desc">When membership starts (DD-MM-YYYY)</span>
            </div>
            <div className="field-item required">
              <span className="field-name">End Date</span>
              <span className="field-desc">When membership ends (DD-MM-YYYY)</span>
            </div>
            <div className="field-item required">
              <span className="field-name">Payment Date</span>
              <span className="field-desc">When payment was made (DD-MM-YYYY)</span>
            </div>
          </div>
        </div>

        {/* Optional Fields */}
        <div className="fields-section">
          <h5 className="fields-title optional-title">Optional Information</h5>
          <div className="fields-list compact">
            <span className="optional-field">Name</span>
            <span className="optional-field">Email</span>
            <span className="optional-field">Price</span>
          </div>
        </div>

        {/* Visual Example */}
        <div className="csv-example-section">
          <h5 className="example-title">Example: How Your File Should Look</h5>
          <div className="csv-example-table-wrapper">
            <table className="csv-example-table">
              <thead>
                <tr>
                  <th>mobile</th>
                  <th>name</th>
                  <th>email</th>
                  <th>plan</th>
                  <th>price</th>
                  <th>start_date</th>
                  <th>end_date</th>
                  <th>payment_date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>9876543210</td>
                  <td>Rahul Sharma</td>
                  <td>rahul@email.com</td>
                  <td>Monthly</td>
                  <td>2999</td>
                  <td>05-02-2026</td>
                  <td>05-03-2026</td>
                  <td>05-02-2026</td>
                </tr>
                <tr>
                  <td>8765432109</td>
                  <td>Priya Patel</td>
                  <td>priya@email.com</td>
                  <td>Quarterly</td>
                  <td>7999</td>
                  <td>01-02-2026</td>
                  <td>01-05-2026</td>
                  <td>01-02-2026</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="example-note">
            This is Row 1 (header) and Rows 2-4 (member data). You can have any number of rows.
          </p>
        </div>

        {/* Tips */}
        <div className="upload-tips">
          <h5 className="tips-title">Tips</h5>
          <ul>
            <li>The <strong>plan name</strong> in your file will be matched to actual plans in the next step</li>
            <li>Empty cells are okay for optional fields</li>
            <li>Make sure dates are in <strong>DD-MM-YYYY</strong> format (e.g., 05-02-2026)</li>
          </ul>
        </div>
      </div>

      <div className="upload-action-box">
        <div className="formInputBox">
          <label htmlFor="importFile" className="formInputLabel">
            Choose Your File <span className="text-danger">*</span>
          </label>
          <div className="file-input-wrapper">
            <input
              type="file"
              id="importFile"
              className="formInput file-input"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
            />
            <p className="file-hint">Supported: .csv, .xlsx, .xls files</p>
          </div>
          {csvFile && (
            <div className="selected-file-info">
              File selected: <strong>{csvFile.name}</strong>
            </div>
          )}
        </div>
      </div>

      <div className="step-actions">
        <button onClick={handleClose} className="btn-k btn-secondary-k">
          Cancel
        </button>
      </div>
    </div>
  );

  // Render Step 2: Gym Selection + Preview + Plan Mapping (Combined)
  const renderPreviewStep = () => {
    const { unmappedPlans } = checkMappingsComplete();
    const allMappingsComplete = uniquePlanValues.length === 0 || unmappedPlans.length === 0;
    const canImport = selectedGym && allMappingsComplete;

    return (
      <div className="member-import-step preview-step">
        {/* File Success Message */}
        <div className="upload-success-box">
          <strong>File loaded successfully!</strong> Found {csvData.length} members to import.
        </div>

        {/* Gym Selection - Required for entire import */}
        <div className="gym-selection-box">
          <h4>Select Gym</h4>
          <p className="section-description">
            All members will be added to this gym.
          </p>
          <div className="formInputBox">
            <SelectDropdown
              options={gymData || []}
              selectedOption={selectedGym || ""}
              setSelectedOption={setSelectedGym}
              name="Select Gym"
            />
          </div>
        </div>

        {/* Plan Mapping Section */}
        {uniquePlanValues.length > 0 && (
          <div className="find-replace-section">
            <h4>Match Plans</h4>
            <p className="section-description">
              Connect the plan names from your file to actual gym plans.
            </p>

            <div className="mapping-box">
              <h5>Plans in your file ({Object.keys(planMappings).length}/{uniquePlanValues.length} matched)</h5>

              <div className="mapping-status">
                {uniquePlanValues.map((val) => (
                  <span
                    key={val}
                    className={`mapping-chip ${planMappings[val] ? "mapped" : "unmapped"}`}
                  >
                    {val} {planMappings[val] && `→ ${planMappings[val].label}`}
                  </span>
                ))}
              </div>

              <div className="mapping-controls">
                <div className="formInputBox">
                  <label className="formInputLabel">Plan from File</label>
                  <SelectDropdown
                    options={uniquePlanValues.map((v) => ({
                      label: `${v} (${csvData.filter((r) => r[columnMapping.plan]?.trim() === v).length} members)`,
                      value: v,
                    }))}
                    selectedOption={selectedPlanValue ? { label: selectedPlanValue, value: selectedPlanValue } : ""}
                    setSelectedOption={(val) => setSelectedPlanValue(val?.value || "")}
                    name="Select Plan from File"
                  />
                </div>

                <div className="formInputBox">
                  <label className="formInputLabel">Match to Gym Plan</label>
                  {loadingPlans ? (
                    <div className="loading-plans">
                      <Loader size="small" />
                      <span>Loading plans...</span>
                    </div>
                  ) : !selectedGym ? (
                    <div className="select-gym-first">
                      Select a gym first to see available plans
                    </div>
                  ) : plansData.length === 0 ? (
                    <div className="no-plans-warning">
                      No plans found for this gym
                    </div>
                  ) : (
                    <SelectDropdown
                      options={plansData}
                      selectedOption={targetPlan || ""}
                      setSelectedOption={setTargetPlan}
                      name="Select Gym Plan"
                    />
                  )}
                </div>

                <button
                  onClick={handleApplyPlanMapping}
                  className="btn-k dark-bg-k apply-btn"
                  disabled={!selectedPlanValue || !targetPlan}
                >
                  Match
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Table with Pagination */}
        <div className="preview-table-section">
          <div className="preview-header">
            <h4>All Members ({csvData.length} total)</h4>
            <div className="pagination-info">
              Showing {Math.min((currentPage - 1) * rowsPerPage + 1, csvData.length)} - {Math.min(currentPage * rowsPerPage, csvData.length)} of {csvData.length}
            </div>
          </div>

          <div className="table-responsive">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  {columnMapping.mobile && <th>Mobile</th>}
                  {columnMapping.name && <th>Name</th>}
                  {columnMapping.email && <th>Email</th>}
                  {columnMapping.plan && <th>Plan</th>}
                  {columnMapping.start_date && <th>Start Date</th>}
                  {columnMapping.end_date && <th>End Date</th>}
                  {columnMapping.payment_date && <th>Payment Date</th>}
                  {columnMapping.price && <th>Price</th>}
                </tr>
              </thead>
              <tbody>
                {csvData
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map((row, index) => {
                    const actualIndex = (currentPage - 1) * rowsPerPage + index;
                    const planCsvValue = columnMapping.plan ? row[columnMapping.plan]?.trim() : "";
                    const mappedPlan = planMappings[planCsvValue];

                    return (
                      <tr key={row._rowId || actualIndex}>
                        <td>{actualIndex + 1}</td>
                        {columnMapping.mobile && (
                          <td>
                            <input
                              type="text"
                              value={row[columnMapping.mobile] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.mobile, e.target.value)
                              }
                              className="preview-input"
                            />
                          </td>
                        )}
                        {columnMapping.name && (
                          <td>
                            <input
                              type="text"
                              value={row[columnMapping.name] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.name, e.target.value)
                              }
                              className="preview-input"
                            />
                          </td>
                        )}
                        {columnMapping.email && (
                          <td>
                            <input
                              type="email"
                              value={row[columnMapping.email] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.email, e.target.value)
                              }
                              className="preview-input"
                            />
                          </td>
                        )}
                        {columnMapping.plan && (
                          <td>
                            <div className="mapped-value">
                              <span className="csv-value">{planCsvValue || "-"}</span>
                              {mappedPlan && (
                                <>
                                  <span className="arrow">→</span>
                                  <span className="mapped-label success">{mappedPlan.label}</span>
                                </>
                              )}
                              {!mappedPlan && planCsvValue && (
                                <span className="mapped-label warning">Not matched</span>
                              )}
                            </div>
                          </td>
                        )}
                        {columnMapping.start_date && (
                          <td>
                            <input
                              type="text"
                              value={row[columnMapping.start_date] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.start_date, e.target.value)
                              }
                              className="preview-input"
                              placeholder="DD-MM-YYYY"
                            />
                          </td>
                        )}
                        {columnMapping.end_date && (
                          <td>
                            <input
                              type="text"
                              value={row[columnMapping.end_date] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.end_date, e.target.value)
                              }
                              className="preview-input"
                              placeholder="DD-MM-YYYY"
                            />
                          </td>
                        )}
                        {columnMapping.payment_date && (
                          <td>
                            <input
                              type="text"
                              value={row[columnMapping.payment_date] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.payment_date, e.target.value)
                              }
                              className="preview-input"
                              placeholder="DD-MM-YYYY"
                            />
                          </td>
                        )}
                        {columnMapping.price && (
                          <td>
                            <input
                              type="number"
                              value={row[columnMapping.price] || ""}
                              onChange={(e) =>
                                handleRowFieldChange(actualIndex, columnMapping.price, e.target.value)
                              }
                              className="preview-input"
                              placeholder="0"
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {csvData.length > rowsPerPage && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-pages">
                {(() => {
                  const totalPages = Math.ceil(csvData.length / rowsPerPage);
                  const pages = [];
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, currentPage + 2);

                  // Adjust to always show 5 pages if possible
                  if (endPage - startPage < 4) {
                    if (startPage === 1) {
                      endPage = Math.min(totalPages, 5);
                    } else {
                      startPage = Math.max(1, totalPages - 4);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`pagination-btn page-num ${currentPage === i ? "active" : ""}`}
                        onClick={() => setCurrentPage(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
              </div>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(csvData.length / rowsPerPage)))}
                disabled={currentPage === Math.ceil(csvData.length / rowsPerPage)}
              >
                Next
              </button>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.ceil(csvData.length / rowsPerPage))}
                disabled={currentPage === Math.ceil(csvData.length / rowsPerPage)}
              >
                Last
              </button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="import-summary">
          <h4>Ready to Import?</h4>
          <div className="summary-checklist">
            <div className={`checklist-item ${selectedGym ? "done" : "pending"}`}>
              {selectedGym ? "✓" : "○"} Gym selected {selectedGym && `(${selectedGym.label})`}
            </div>
            <div className={`checklist-item ${allMappingsComplete ? "done" : "pending"}`}>
              {allMappingsComplete ? "✓" : "○"} All plans matched ({Object.keys(planMappings).length}/{uniquePlanValues.length})
            </div>
            <div className="checklist-item done">
              ✓ {csvData.length} members to import
            </div>
          </div>

          {!allMappingsComplete && (
            <div className="mapping-warning">
              <strong>Please match all plans before importing.</strong>
              <div>Unmatched: {unmappedPlans.join(", ")}</div>
            </div>
          )}

          {/* Replace existing toggle — admin opt-in to overwrite old imported subs */}
          <div className="replace-existing-box" style={{ marginTop: "16px", padding: "12px", border: "1px solid #e0e0e0", borderRadius: "6px", background: replaceExisting ? "#fff8e1" : "#fafafa" }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                style={{ marginTop: "3px" }}
              />
              <div>
                <div style={{ fontWeight: 600 }}>Replace existing imported subscriptions</div>
                <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                  {replaceExisting
                    ? "⚠️ All previously imported subscriptions for these members in this gym will be DELETED before re-import. Use this to fix incorrect data."
                    : "Default: existing subscriptions will be preserved. Only exact-duplicate periods will be skipped — overlapping periods will be inserted as additional active subscriptions."}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="step-actions">
          <button onClick={() => setStep(1)} className="btn-k btn-secondary-k">
            Back
          </button>
          <button
            onClick={handlePreview}
            className="btn-k dark-bg-k"
            disabled={!canImport || isPreviewLoading}
          >
            {isPreviewLoading ? (
              <>
                <Loader size="small" color="#ffffff" />
                <span>Analyzing...</span>
              </>
            ) : (
              `Preview & Validate (${csvData.length} rows)`
            )}
          </button>
        </div>
      </div>
    );
  };

  // Render Step 3: Preview Summary (dry-run result before actual import)
  const renderPreviewSummaryStep = () => {
    const data = previewData || {};
    const breakdown = data.skip_breakdown || {};
    const skipReasonLabels = {
      exact_duplicate: "Exact duplicate (same start & end already exists in this gym)",
      exact_duplicate_in_batch: "Exact duplicate within this same upload",
      other: "Other",
    };

    return (
      <div className="member-import-step preview-summary-step">
        <div className="upload-success-box" style={{ background: "#e3f2fd", borderColor: "#90caf9" }}>
          <strong>Preview ready.</strong> No data has been written yet — review below before confirming.
        </div>

        {/* Top stats */}
        <div className="result-stat-row" style={{ marginTop: "16px" }}>
          <div className="result-stat success">
            <span className="result-stat-value">{data.would_insert_count || 0}</span>
            <span className="result-stat-label">Will Import</span>
          </div>
          <div className="result-stat warning">
            <span className="result-stat-value">{data.would_skip_count || 0}</span>
            <span className="result-stat-label">Will Skip</span>
          </div>
          <div className="result-stat error">
            <span className="result-stat-value">{data.failed_count || 0}</span>
            <span className="result-stat-label">Invalid Rows</span>
          </div>
          {data.replace_existing && (
            <div className="result-stat" style={{ background: "#fff3e0", color: "#e65100" }}>
              <span className="result-stat-value">{data.would_replace_users_count || 0}</span>
              <span className="result-stat-label">Members to Replace</span>
            </div>
          )}
        </div>

        {/* Replace existing notice */}
        {data.replace_existing && (
          <div className="mapping-warning" style={{ marginTop: "16px", background: "#fff3e0", border: "1px solid #ffb74d" }}>
            <strong>⚠️ Replace mode is ON.</strong>
            <div>
              For {data.would_replace_users_count || 0} member(s) already in this gym, ALL previously imported
              subscriptions will be DELETED first, then fresh data inserted from your file.
              Manually-added (non-imported) subscriptions are NOT affected.
            </div>
          </div>
        )}

        {/* Skip breakdown by reason */}
        {data.would_skip_count > 0 && (
          <div className="result-details-box" style={{ marginTop: "16px" }}>
            <h5>Why are rows being skipped?</h5>
            <div className="result-list">
              {Object.entries(breakdown).map(([reason, count]) => (
                <div key={reason} className="result-item warning" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{skipReasonLabels[reason] || reason}</span>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skipped sample list */}
        {data.would_skip && data.would_skip.length > 0 && (
          <div className="result-details-box" style={{ marginTop: "16px" }}>
            <h5>Skipped rows (showing first {Math.min(data.would_skip.length, 10)} of {data.would_skip.length})</h5>
            <div className="result-list">
              {data.would_skip.slice(0, 10).map((item, idx) => (
                <div key={idx} className="result-item warning">
                  <span className="result-item-mobile">{item.mobile}</span>
                  <span className="result-item-reason">{item.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Failed (invalid input) sample list */}
        {data.failed && data.failed.length > 0 && (
          <div className="result-details-box" style={{ marginTop: "16px" }}>
            <h5>Invalid rows (showing first {Math.min(data.failed.length, 10)} of {data.failed.length})</h5>
            <div className="result-list">
              {data.failed.slice(0, 10).map((item, idx) => (
                <div key={idx} className="result-item error">
                  <span className="result-item-mobile">{item.mobile}</span>
                  <span className="result-item-reason">{item.reason || item.error}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="step-actions">
          <button onClick={() => setStep(2)} className="btn-k btn-secondary-k">
            Back to Edit
          </button>
          <button
            onClick={handleImport}
            className="btn-k dark-bg-k"
            disabled={isImporting || (data.would_insert_count || 0) === 0}
          >
            {isImporting ? (
              <>
                <Loader size="small" color="#ffffff" />
                <span>Importing...</span>
              </>
            ) : (
              `Confirm & Import ${data.would_insert_count || 0} Members`
            )}
          </button>
        </div>
      </div>
    );
  };

  // Render Step 3: Results
  const renderResultsStep = () => {
    const { data } = importResults || {};

    return (
      <div className="member-import-step results-step">
        <div className={`result-box ${importResults?.success ? "success" : "error"}`}>
          <h4>{importResults?.success ? "Import Complete!" : "Import Failed"}</h4>
          <p>{importResults?.message}</p>
        </div>

        {data && (
          <div className="import-results-details">
            <div className="result-stat-row">
              <div className="result-stat success">
                <span className="result-stat-value">{data.success_count || 0}</span>
                <span className="result-stat-label">Imported</span>
              </div>
              <div className="result-stat warning">
                <span className="result-stat-value">{data.skipped_count || 0}</span>
                <span className="result-stat-label">Skipped</span>
              </div>
              <div className="result-stat error">
                <span className="result-stat-value">{data.failed_count || 0}</span>
                <span className="result-stat-label">Failed</span>
              </div>
            </div>

            {/* Skipped Details */}
            {data.skipped && data.skipped.length > 0 && (
              <div className="result-details-box">
                <h5>Skipped ({data.skipped.length})</h5>
                <div className="result-list">
                  {data.skipped.slice(0, 5).map((item, index) => (
                    <div key={index} className="result-item warning">
                      <span className="result-item-mobile">{item.mobile}</span>
                      <span className="result-item-reason">{item.reason}</span>
                    </div>
                  ))}
                  {data.skipped.length > 5 && (
                    <p className="more-items">... and {data.skipped.length - 5} more</p>
                  )}
                </div>
              </div>
            )}

            {/* Failed Details */}
            {data.failed && data.failed.length > 0 && (
              <div className="result-details-box">
                <h5>Failed ({data.failed.length})</h5>
                <div className="result-list">
                  {data.failed.slice(0, 5).map((item, index) => (
                    <div key={index} className="result-item error">
                      <span className="result-item-mobile">{item.mobile}</span>
                      <span className="result-item-reason">{item.reason || item.error}</span>
                    </div>
                  ))}
                  {data.failed.length > 5 && (
                    <p className="more-items">... and {data.failed.length - 5} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="step-actions">
          <button onClick={handleClose} className="btn-k dark-bg-k">
            Done
          </button>
          <button
            onClick={() => {
              setStep(1);
              setPreviewData(null);
              setImportResults(null);
            }}
            className="btn-k btn-secondary-k"
          >
            Import Another File
          </button>
        </div>
      </div>
    );
  };

  return (
    <ReactModal ariaHideApp={false} isOpen={isOpen} onRequestClose={handleClose}>
      <div className="modal-body-k large rounded-5 bg-white member-import-modal">
        <ModalHeader
          title={`Import Members - Step ${step} of 4`}
          onClick={handleClose}
        />
        <div className="member-import-content">
          {step === 1 && renderUploadStep()}
          {step === 2 && renderPreviewStep()}
          {step === 3 && renderPreviewSummaryStep()}
          {step === 4 && renderResultsStep()}
        </div>
      </div>
    </ReactModal>
  );
};

export default MemberImportModal;
